import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import axios from "axios";
import { Dropdown, Modal } from "react-bootstrap";
import { MoreVert } from "@mui/icons-material";
import ENDPOINT from "../../context/EndPoint";
import SecurityHeaders from "../../context/SecurityHeaders";
import { AuthContext } from "../../context/AuthContext";
import GetTestUsers from "../../actions/quiz/GetTestUsers";
import GetUserTestDetail from "../../actions/quiz/GetUserTestDetail";
import ResetTestAttempt from "../../actions/quiz/ResetTestAttempt";

// caret-free 3-dots toggle
const DotsToggle = React.forwardRef(({ onClick }, ref) => (
  <span ref={ref} onClick={(e) => { e.preventDefault(); onClick(e); }} style={{ cursor: "pointer" }}>
    <MoreVert />
  </span>
));

const statusBadge = (s) => {
  const map = {
    completed: { bg: "#eafff3", color: "#127a4a", label: "Completed" },
    pending:   { bg: "#fff7e6", color: "#9a6700", label: "In progress" },
  };
  const m = map[s] || { bg: "#eef1f5", color: "#555", label: s || "—" };
  return <span style={{ background: m.bg, color: m.color, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>{m.label}</span>;
};

const MockResults = ({ test_type = "mock-test" }) => {
  const params = useParams();
  const course_url = params.course_url;
  const { user } = useContext(AuthContext);

  const [papers, setPapers] = useState([]);
  const [selectedPaper, setSelectedPaper] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detail, setDetail] = useState(null); // {user, data}

  const loadPapers = async () => {
    try {
      const data = new FormData();
      data.append("session", user.SessionId);
      data.append("course_url", course_url);
      data.append("test_type", test_type);
      const res = await axios.post(ENDPOINT + "mock-test-categories.php", data, SecurityHeaders);
      if (res && res.data && res.data.status === "success") setPapers(res.data.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (user && user.SessionId) loadPapers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async (testCode) => {
    setLoadingUsers(true);
    setUsers([]);
    try {
      const res = await GetTestUsers({ payload: { session: user.SessionId, test_code: testCode, course_url } });
      if (res && res.status === "success") setUsers(res.users || []);
    } catch (e) { /* ignore */ }
    setLoadingUsers(false);
  };

  const onSelectPaper = (testCode) => {
    setSelectedPaper(testCode);
    if (testCode) loadUsers(testCode);
    else setUsers([]);
  };

  const viewDetail = async (u) => {
    setDetail({ user: u, data: null });
    setDetailOpen(true);
    setLoadingDetail(true);
    try {
      const res = await GetUserTestDetail({ payload: { session: user.SessionId, test_code: selectedPaper, user_id: u.user_id } });
      if (res && res.status === "success") setDetail({ user: u, data: res });
    } catch (e) { /* ignore */ }
    setLoadingDetail(false);
  };

  const resetAttempt = async (u, label) => {
    if (!window.confirm(`${label} — ${u.first_name} ${u.last_name}? This clears their attempt for this test so they can take it again.`)) return;
    try {
      const res = await ResetTestAttempt({ payload: { session: user.SessionId, test_code: selectedPaper, user_id: u.user_id } });
      if (res && res.status === "success") {
        window.alert(
          `${label} done — cleared ${res.history_removed} attempt record(s) and ${res.answers_removed} answer(s). ` +
          `${u.first_name} can now retake this test (they may need to reload the exam page).`
        );
        loadUsers(selectedPaper);
      } else {
        window.alert((res && res.message) || "Failed to update attempt.");
      }
    } catch (e) { window.alert(e.message); }
  };

  const paperTitle = (p) => p.title || `#${p.test_code}`;

  return (
    <div className="container-fluid p-3">
      <h5 className="mb-1" style={{ fontWeight: 700 }}>Mock Results</h5>
      <p className="text-muted" style={{ fontSize: 13 }}>Select a test paper to see who attempted it.</p>

      <label className="form-label mt-2 mb-1" style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>Test paper</label>
      <select className="form-control col-lg-6" style={{ maxWidth: 460 }} value={selectedPaper} onChange={(e) => onSelectPaper(e.target.value)}>
        <option value="">-- Select a test --</option>
        {papers.map((p) => (
          <option key={p.test_code} value={p.test_code}>{paperTitle(p)} (#{p.test_code})</option>
        ))}
      </select>

      <div className="mt-4">
        {!selectedPaper ? (
          <p className="text-muted">Pick a test paper above.</p>
        ) : loadingUsers ? (
          <p className="text-muted">Loading attempts…</p>
        ) : users.length === 0 ? (
          <p className="text-muted">No user has attempted this test yet.</p>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr style={{ fontSize: 13, color: "#666" }}>
                  <th>Student</th>
                  <th>Status</th>
                  <th>Score</th>
                  <th>Attempted</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id}>
                    <td>
                      <div className="d-flex align-items-center" style={{ gap: 10 }}>
                        <img
                          src={u.profile_pic || "https://skillsuprise.com/resources/images/default.png"}
                          alt=""
                          style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", background: "#eee" }}
                          onError={(e) => { e.target.style.visibility = "hidden"; }}
                        />
                        <div>
                          <div style={{ fontWeight: 600 }}>{(u.first_name || "") + " " + (u.last_name || "")}</div>
                          <div style={{ fontSize: 12, color: "#888" }}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{statusBadge(u.status)}</td>
                    <td><b>{u.correct_count}</b> / {u.total_questions} <span style={{ color: "#888", fontSize: 12 }}>({u.score}%)</span></td>
                    <td>{u.attempted_questions} / {u.total_questions}</td>
                    <td className="text-end">
                      <Dropdown align="end">
                        <Dropdown.Toggle as={DotsToggle} />
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => viewDetail(u)}>View details</Dropdown.Item>
                          <Dropdown.Item onClick={() => resetAttempt(u, "Attempt again")}>Attempt again</Dropdown.Item>
                          <Dropdown.Item className="text-danger" onClick={() => resetAttempt(u, "Delete")}>Delete</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* detail modal */}
      <Modal show={detailOpen} onHide={() => setDetailOpen(false)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            {detail && detail.user ? `${detail.user.first_name} ${detail.user.last_name} — answers` : "Answers"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetail ? (
            <p className="text-muted">Loading…</p>
          ) : detail && detail.data ? (
            <>
              <div className="d-flex flex-wrap mb-3" style={{ gap: 16 }}>
                <span>Score: <b>{detail.data.correct_count}</b> / {detail.data.total_questions} ({detail.data.score}%)</span>
              </div>
              {detail.data.questions.map((q, idx) => (
                <div key={q.quiz_id} className="border rounded p-3 mb-3">
                  <div className="d-flex align-items-start" style={{ gap: 10 }}>
                    <span style={{ fontWeight: 700, color: "#555" }}>{idx + 1}.</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>{q.question}</div>
                      {[1, 2, 3, 4].map((n) => {
                        const f = `option${n}`;
                        const text = q[f];
                        if (!text) return null;
                        const isCorrect = q.correct_field === f;
                        const isSelected = q.selected_field === f;
                        let bg = "#fff", color = "#333", border = "#e3e6eb", tag = "";
                        if (isCorrect) { bg = "#eafff3"; border = "#27e89d"; color = "#127a4a"; tag = "  ✓ correct answer"; }
                        if (isSelected && !isCorrect) { bg = "#fff0f0"; border = "#ff4d4f"; color = "#b42318"; tag = "  ✗ selected"; }
                        else if (isSelected && isCorrect) { tag = "  ✓ selected (correct)"; }
                        return (
                          <div key={f} style={{ border: `1px solid ${border}`, background: bg, color, borderRadius: 8, padding: "6px 10px", marginBottom: 6, fontSize: 14 }}>
                            {text}<span style={{ fontSize: 12, fontWeight: 600 }}>{tag}</span>
                          </div>
                        );
                      })}
                      <div style={{ fontSize: 12.5, marginTop: 4 }}>
                        <span style={{ color: q.correct ? "#127a4a" : (q.selected_option ? "#b42318" : "#888"), fontWeight: 600 }}>
                          {q.selected_option ? (q.correct ? "Correct" : "Incorrect") : "Not attempted"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <p className="text-muted">No details.</p>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MockResults;
